import React, { useState, useEffect, useRef } from 'react';
import { FaTimes, FaExternalLinkAlt, FaExclamationTriangle, FaSpinner } from 'react-icons/fa';
import { SiZerodha } from 'react-icons/si';
import { useAuth } from '../context/AuthContext';
import api from '../api';
import ZerodhaBrokerErrorAlert from './ZerodhaBrokerErrorAlert';
import { getBrokerErrorMessage, isZerodhaApiKeyRejectedError } from '../utils/zerodhaBrokerErrors';

const ReconnectBrokerModal = () => {
  const { 
    brokerReconnectRequired, 
    clearBrokerReconnect,
    lastFailedRequest
  } = useAuth();
  
  const [error, setError] = useState('');
  const [lastError, setLastError] = useState(null);
  const [errorCode, setErrorCode] = useState(null);
  const [isReconnecting, setIsReconnecting] = useState(false);

  const showApiKeyRejectedUx =
    isZerodhaApiKeyRejectedError(lastError) || errorCode === 'zerodha_api_key_rejected';
  const [reconnectPopup, setReconnectPopup] = useState(null);
  const reconnectionSuccessfulRef = useRef(false);

  // Reset when modal state changes
  useEffect(() => {
    if (!brokerReconnectRequired) {
      // Clean up when modal closes
      if (reconnectPopup && !reconnectPopup.closed) {
        reconnectPopup.close();
      }
      // Reset success flag when modal closes
      reconnectionSuccessfulRef.current = false;
    }
  }, [brokerReconnectRequired, reconnectPopup]);

  // Listen for reconnect success message from popup
  useEffect(() => {
    console.log('📡 Message listener registered for Zerodha reconnection');
    
    const handleMessage = async (event) => {
      // Verify origin for security (silently ignore untrusted origins)
      if (event.origin !== window.location.origin) {
        return;
      }
      
      console.log('📨 Message received:', event.data, 'from origin:', event.origin);
      
      // Handle both ZERODHA_AUTH_SUCCESS (from callback page) and ZERODHA_RECONNECT_SUCCESS
      if (event.data.type === 'ZERODHA_AUTH_SUCCESS' || event.data.type === 'ZERODHA_RECONNECT_SUCCESS') {
        console.log('✅✅✅ Zerodha reconnection successful! Setting success flag...');
        console.log('📝 Message type received:', event.data.type);
        
        // Mark reconnection as successful to prevent "cancelled" error
        reconnectionSuccessfulRef.current = true;
        console.log('✅ Success flag set to:', reconnectionSuccessfulRef.current);
        
        // Close the reconnect popup
        if (reconnectPopup && !reconnectPopup.closed) {
          console.log('🚪 Closing reconnect popup...');
          reconnectPopup.close();
        }
        
        // Wait a brief moment for popup to fully close before clearing state
        setTimeout(() => {
          console.log('🧹 Clearing broker reconnect state and closing modal...');
          
          // Dispatch custom event for page-level components to listen
          window.dispatchEvent(new CustomEvent('broker-reconnect-success'));
          
          // Clear the reconnect state (this closes the modal)
          clearBrokerReconnect();
          setIsReconnecting(false);
          
          // Optionally retry the failed request if available
          if (lastFailedRequest) {
            console.log('🔄 Retrying failed request:', lastFailedRequest);
            
            api({
              method: lastFailedRequest.method,
              url: lastFailedRequest.url,
              data: lastFailedRequest.data,
              params: lastFailedRequest.params
            }).then(() => {
              console.log('✅ Successfully retried the failed request');
            }).catch((retryError) => {
              console.error('❌ Failed to retry request:', retryError);
              // If retry fails, user can manually refresh
            });
          }
        }, 100);
      }
      
      // Handle error messages from callback page
      if (event.data.type === 'ZERODHA_AUTH_ERROR') {
        console.log('❌ Zerodha authentication failed:', event.data.error);
        
        // Mark as unsuccessful
        reconnectionSuccessfulRef.current = false;
        
        // Close the reconnect popup
        if (reconnectPopup && !reconnectPopup.closed) {
          console.log('🚪 Closing reconnect popup due to error...');
          reconnectPopup.close();
        }
        
        // Show error message
        setLastError(null);
        setErrorCode(event.data.code || null);
        setError(event.data.error || 'Authentication failed. Please try again.');
        setIsReconnecting(false);
      }
    };

    window.addEventListener('message', handleMessage);
    console.log('✅ Message listener attached');
    
    return () => {
      console.log('🔴 Message listener removed');
      window.removeEventListener('message', handleMessage);
    };
  }, [reconnectPopup, clearBrokerReconnect, lastFailedRequest]);

  const handleReconnect = async () => {
    setError('');
    setLastError(null);
    setErrorCode(null);
    setIsReconnecting(true);
    reconnectionSuccessfulRef.current = false; // Reset success flag

    try {
      await api.get('/api/v1/brokers/zerodha/login-url');

      const oauthStartUrl = `${window.location.origin}/brokers/zerodha/oauth-start`;
      console.log('🔗 Opening Zerodha OAuth launcher:', oauthStartUrl);

      const width = 600;
      const height = 700;
      const left = window.screen.width / 2 - width / 2;
      const top = window.screen.height / 2 - height / 2;

      const popup = window.open(
        oauthStartUrl,
        'zerodha_login',
        `width=${width},height=${height},left=${left},top=${top},toolbar=no,menubar=no,scrollbars=yes,resizable=yes`
      );

      if (!popup) {
        throw new Error('Failed to open popup. Please allow popups for this site.');
      }

      setReconnectPopup(popup);

      console.log('👀 Starting popup monitor...');

      // Monitor if popup is closed manually
      const popupCheckInterval = setInterval(() => {
        if (popup.closed) {
          console.log('🚪 Popup closed detected by monitor');
          clearInterval(popupCheckInterval);
          setIsReconnecting(false);
          
          console.log('⏳ Waiting 1500ms for success message...', {
            currentSuccessFlag: reconnectionSuccessfulRef.current,
            brokerReconnectRequired
          });
          
          // Wait for success message to arrive (if any)
          // Give it more time to receive the postMessage
          setTimeout(() => {
            console.log('⏰ Timeout fired after popup close. Checking success flag:', {
              reconnectionSuccessful: reconnectionSuccessfulRef.current,
              brokerReconnectRequired
            });
            
            // Only show cancelled error if reconnection was NOT successful
            // This prevents false "cancelled" errors when reconnection actually succeeded
            if (brokerReconnectRequired && !reconnectionSuccessfulRef.current) {
              console.log('⚠️⚠️⚠️ Popup closed without success message - showing cancelled error');
              setError('Reconnection was cancelled. Please try again when ready.');
            } else if (reconnectionSuccessfulRef.current) {
              console.log('✅✅✅ Reconnection successful - NOT showing cancelled error');
            } else {
              console.log('ℹ️ brokerReconnectRequired is false, modal should be closed already');
            }
          }, 1500); // Increased from 1000ms to 1500ms to give even more time for message
        }
      }, 500);

    } catch (err) {
      console.error('❌ Reconnection error:', err);
      setLastError(err);
      setError(
        getBrokerErrorMessage(err, 'Failed to start reconnection. Please try again.')
      );
      setIsReconnecting(false);
    }
  };

  const handleClose = () => {
    if (reconnectPopup && !reconnectPopup.closed) {
      reconnectPopup.close();
    }
    clearBrokerReconnect();
    setIsReconnecting(false);
  };

  if (!brokerReconnectRequired) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <SiZerodha className="text-3xl text-blue-600" />
            <div>
              <h2 className="text-xl font-bold text-gray-900">Zerodha Session Expired</h2>
              <p className="text-sm text-gray-600">Reconnection Required</p>
            </div>
          </div>
          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <FaTimes className="text-2xl" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          {error && (
            showApiKeyRejectedUx ? (
              <ZerodhaBrokerErrorAlert message={error} err={lastError} className="mb-4" />
            ) : (
              <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg text-red-800 text-sm">
                {error}
              </div>
            )
          )}

          <div className="space-y-6">
            <div className="flex justify-center">
              <div className="w-16 h-16 bg-yellow-100 rounded-full flex items-center justify-center">
                <FaExclamationTriangle className="text-3xl text-yellow-600" />
              </div>
            </div>

            <div className="text-center">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                {showApiKeyRejectedUx
                  ? 'Zerodha API Key Rejected'
                  : 'Daily Re-authentication Required'}
              </h3>
              <p className="text-gray-600 text-sm mb-4">
                {showApiKeyRejectedUx
                  ? 'Reconnect cannot continue until your Kite Connect app is active and billing is current.'
                  : 'Your Zerodha access token has expired. Zerodha tokens are valid for one day only and need to be refreshed daily for security.'}
              </p>
            </div>

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <h4 className="font-semibold text-blue-900 mb-2 text-sm">What happens next?</h4>
              <ol className="text-xs text-blue-800 space-y-1 list-decimal list-inside">
                <li>Click "Reconnect Now" below</li>
                <li>Log in with your Zerodha credentials</li>
                <li>Your request will be automatically retried</li>
              </ol>
            </div>

            <div className="space-y-3">
              <button
                onClick={handleReconnect}
                disabled={isReconnecting}
                className="w-full bg-blue-600 text-white py-3 px-4 rounded-lg font-medium hover:bg-blue-700 transition-colors flex items-center justify-center disabled:bg-blue-400 disabled:cursor-not-allowed"
              >
                {isReconnecting ? (
                  <>
                    <FaSpinner className="animate-spin mr-2" />
                    Opening Zerodha Login...
                  </>
                ) : (
                  <>
                    Reconnect Now
                    <FaExternalLinkAlt className="ml-2" />
                  </>
                )}
              </button>

              <button
                onClick={handleClose}
                disabled={isReconnecting}
                className="w-full text-gray-600 hover:text-gray-800 text-sm py-2 disabled:text-gray-400 disabled:cursor-not-allowed"
              >
                Remind Me Later
              </button>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="border-t border-gray-200 px-6 py-4 bg-gray-50">
          <p className="text-xs text-gray-600 text-center">
            🔒 Your credentials remain secure. Only your access token needs to be refreshed daily.
          </p>
        </div>
      </div>
    </div>
  );
};

export default ReconnectBrokerModal;

